import { useEffect, useState } from "react";
import { View } from "react-native";
import H4 from "../../components/Typography/H4";
import H5 from "../../components/Typography/H5";
import Image from "../../components/Image/Image";
import SettingsContainer from "../../components/Layout/SettingsContainer";
import GradientCodeComponent from "../../components/GenerateCodeComponent";
import useToggleStore from "../../store/useToggle.store";
import { signInCode } from "../../services/accountService";
import { globalAny } from "../../utils/Utils";
import { palladium } from "../../StyleHelpers";
import AndroidQr from "../../assets/Images/android_qr.png";
import IosQr from "../../assets/Images/ios_qr.png";
import {
	StyledAddScreenContainer,
	StyledContainerAddScreen,
	StyledNarrowAddScreen,
	StyledNarrowContainerAddScreen,
	StyledText,
	StyledTextContainer,
	StyledTitle,
} from "./styled";
import useGenericContentStore from "../../store/genericContent.store";

const SettingsAddScreen = () => {
	const [isGenerating, setIsGenerating] = useState(false);
	const [code, setCode] = useState("");
	const [counter, setCounter] = useState(1);
	const [disabled, setDisabled] = useState(false);
	const [isTimerRunning, setIsTimerRunning] = useState(false);
	const { isToggleQR } = useToggleStore((state: any) => ({
		isToggleQR: state.isToggleQR,
	}));
	const { isNarrow } = useGenericContentStore((state) => ({
		isNarrow: state.isNarrow,
	}));
	/**
	 * Generates a sign-in code asynchronously.
	 * Disables the generate button and shows loading state while generating.
	 * If successful, sets the code and starts the countdown timer.
	 * Re-enables the generate button after completion.
	 */
	const handleGenerateCode = async () => {
		setDisabled(true);
		setIsGenerating(true);

		const result: any = await signInCode();
		if (result?.code) {
			setCode(result.code);
			setIsTimerRunning(true);
		}

		setDisabled(false);
	};

	/**
	 * Renders a title section with a main title, description, and optional sub-description.
	 *
	 * @param {string} title - The main title text.
	 * @param {string} desc - The primary description text.
	 * @param {string} [subDesc] - Optional secondary description text.
	 * @returns {JSX.Element} - The rendered title component.
	 */
	const renderTitle = (title: string, desc: string, subDesc?: string) => (
		<View>
			{/* @ts-ignore */}
			<StyledTitle>
				<H4>{title}</H4>
			</StyledTitle>
			{/* @ts-ignore */}
			<StyledTextContainer isNarrow={isNarrow}>
				<H5 fontColor={palladium}>
					{/* @ts-ignore */}
					<StyledText>{desc}</StyledText>
					{/* @ts-ignore */}
					{subDesc && <StyledText>{subDesc}</StyledText>}
				</H5>
			</StyledTextContainer>
		</View>
	);

	/**
	 * Renders a narrow layout section containing an image and a title block.
	 *
	 * @param {string} title - The main title text.
	 * @param {string} desc - The primary description text.
	 * @param {any} image - Image source URI or object.
	 * @param {string} link - Optional link or sub-description text.
	 * @returns {JSX.Element} - The rendered narrow section.
	 */
	const renderQRNarrowSection = (title: string, desc: string, image: any, link: string) => (
		<SettingsContainer>
			{/* @ts-ignore */}
			<StyledNarrowAddScreen>
				<Image source={{ uri: image }} width={160} height={160} resizeMode="contain" />
				{renderTitle(title, desc, link)}
			</StyledNarrowAddScreen>
		</SettingsContainer>
	);

	/**
	 * Renders a wide layout section containing a title block and an image side-by-side.
	 *
	 * @param {string} title - The main title text.
	 * @param {string} desc - The primary description text.
	 * @param {any} image - Image source URI or object.
	 * @param {string} link - Optional link or sub-description text.
	 * @returns {JSX.Element} - The rendered wide section.
	 */
	const renderQRWideSection = (title: string, desc: string, image: any, link: string) => (
		<SettingsContainer>
			{/* @ts-ignore */}
			<StyledContainerAddScreen>
				{renderTitle(title, desc, link)}
				<Image source={{ uri: image }} width={160} height={160} resizeMode="contain" />
			</StyledContainerAddScreen>
		</SettingsContainer>
	);

	/**
	 * Handles the countdown timer for the generated code.
	 * Decrements the counter every second while the timer is running.
	 * Resets the timer, code, and generating state once the counter reaches zero.
	 */
	useEffect(() => {
		if (isTimerRunning && counter > 0) {
			const timerId = setTimeout(() => {
				setCounter((prev) => prev - 0.017);
			}, 1000);

			return () => clearTimeout(timerId);
		}

		if (counter <= 0) {
			setIsTimerRunning(false);
			setCounter(1);
			setIsGenerating(false);
			setCode("");
		}
	}, [counter, isTimerRunning]);

	return (
		//@ts-ignore
		<StyledAddScreenContainer>
			<SettingsContainer>
				{/* @ts-ignore */}
				<StyledContainerAddScreen>
					{renderTitle(globalAny.language.sign_in_with_code, globalAny.language.sign_in_with_code_desc)}
					<GradientCodeComponent
						title={isGenerating ? code : (globalAny.language.generate_code ?? "")}
						onPress={handleGenerateCode}
						hideButton={isGenerating}
						counter={counter}
						disabled={disabled}
						isMobile={false}
					/>
				</StyledContainerAddScreen>
			</SettingsContainer>

			{isToggleQR &&
				!isNarrow &&
				renderQRWideSection(globalAny.language.download_app, globalAny.language.download_app_desc, IosQr, " www.companionapp.ridevu.com")}

			{isToggleQR &&
				!isNarrow &&
				renderQRWideSection(
					globalAny.language.download_app_android,
					globalAny.language.download_app_desc,
					AndroidQr,
					" www.companionapp.ridevu.com",
				)}

			{isToggleQR && isNarrow && (
				// @ts-ignore
				<StyledNarrowContainerAddScreen>
					<>
						{renderQRNarrowSection(
							globalAny.language.download_app,
							globalAny.language.download_app_desc,
							IosQr,
							" www.companionapp.ridevu.com",
						)}
					</>
					<>
						{renderQRNarrowSection(
							globalAny.language.download_app_android,
							globalAny.language.download_app_desc,
							AndroidQr,
							" www.companionapp.ridevu.com",
						)}
					</>
				</StyledNarrowContainerAddScreen>
			)}
		</StyledAddScreenContainer>
	);
};

export default SettingsAddScreen;
